import googleAnalytics from "./googleAnalytics";
import facebookPixel from "./facebookPixel";
import bingAds from "./bingAds";
import mailchimp from "./mailchimp";

class Analytics {
  constructor() {
    this.initialized = false;
    this.integrations = [googleAnalytics, facebookPixel, bingAds, mailchimp];
    this.queue = [];
    this.methods = ["track", "page", "identify", "reset", "ready"];

    this.setupStubs();
  }

  setupStubs() {
    this.methods.forEach((method) => {
      this[method] = (...args) => {
        if (this.initialized) {
          return this._callMethod(method, args);
        } else {
          this.queue.push({ method, args });
          return this;
        }
      };
    });
  }

  init() {
    if (this.initialized) return;

    if (process.env.NODE_ENV === "development") {
      console.log("Analytics: Skipping initialization in development mode");
      this.initialized = true;
      this._processQueue();
      return;
    }

    try {
      this.integrations.forEach((integration) => {
        integration.init();
      });

      this.initialized = true;
      console.log("Analytics: All integrations initialized");

      this._processQueue();
    } catch (error) {
      console.error("Analytics: Error initializing integrations", error);
    }
  }

  _processQueue() {
    while (this.queue.length > 0) {
      const { method, args } = this.queue.shift();
      this._callMethod(method, args);
    }
  }

  _callMethod(method, args) {
    if (process.env.NODE_ENV === "development") {
      console.log(`Analytics.${method}:`, ...args);
      return this;
    }

    switch (method) {
      case "track":
        return this._track(...args);
      case "page":
        return this._page(...args);
      case "identify":
        return this._identify(...args);
      case "reset":
        return this._reset();
      case "ready":
        return this._ready(...args);
      default:
        console.warn(`Analytics: Unknown method ${method}`);
        return this;
    }
  }

  _track(eventName, properties = {}, options = {}) {
    if (!this.initialized) return this;

    try {
      this.integrations.forEach((integration) => {
        if (integration.track) {
          integration.track(eventName, properties, options);
        }
      });
    } catch (error) {
      console.error("Analytics: Error tracking event", error);
    }

    return this;
  }

  _page(category, name, properties = {}, options = {}) {
    if (!this.initialized) return this;

    let pagePath, pageTitle;

    if (typeof category === "string" && typeof name === "string") {
      pagePath = `${category}/${name}`;
      pageTitle = name;
    } else if (typeof category === "string") {
      pagePath = category;
      pageTitle = category;
    } else if (typeof category === "object") {
      properties = category;
      pagePath = properties.path || window.location.pathname;
      pageTitle = properties.title || document.title;
    }

    try {
      this.integrations.forEach((integration) => {
        if (integration.page) {
          integration.page(pagePath, pageTitle, properties, options);
        }
      });
    } catch (error) {
      console.error("Analytics: Error tracking page", error);
    }

    return this;
  }

  _identify(userId, traits = {}, options = {}) {
    if (!this.initialized) return this;

    try {
      this.integrations.forEach((integration) => {
        if (integration.identify) {
          integration.identify(userId, traits, options);
        }
      });
    } catch (error) {
      console.error("Analytics: Error identifying user", error);
    }

    return this;
  }

  _reset() {
    if (!this.initialized) return this;

    try {
      this.integrations.forEach((integration) => {
        if (integration.reset) {
          integration.reset();
        }
      });
    } catch (error) {
      console.error("Analytics: Error resetting", error);
    }

    return this;
  }

  _ready(callback) {
    if (this.initialized) {
      callback();
    } else {
      const checkReady = setInterval(() => {
        if (this.initialized) {
          clearInterval(checkReady);
          callback();
        }
      }, 100);
    }

    return this;
  }

  trackSubmit(...args) {
    return this.track("Form Submitted", ...args);
  }

  trackClick(...args) {
    return this.track("Clicked", ...args);
  }

  trackLink(...args) {
    return this.track("Link Clicked", ...args);
  }

  trackForm(...args) {
    return this.track("Form Viewed", ...args);
  }

  pageview(...args) {
    return this.page(...args);
  }

  group(...args) {
    console.warn("Analytics: group method not implemented", ...args);
    return this;
  }

  alias(...args) {
    console.warn("Analytics: alias method not implemented", ...args);
    return this;
  }

  debug() {
    console.log("Analytics debug:", {
      initialized: this.initialized,
      queue: this.queue,
      integrations: this.integrations.map((i) => i.constructor.name)
    });
    return this;
  }

  screen(...args) {
    return this.page(...args);
  }

  once(...args) {
    console.warn("Analytics: once method not implemented", ...args);
    return this;
  }

  off(...args) {
    console.warn("Analytics: off method not implemented", ...args);
    return this;
  }

  on(...args) {
    console.warn("Analytics: on method not implemented", ...args);
    return this;
  }

  addSourceMiddleware(...args) {
    console.warn(
      "Analytics: addSourceMiddleware method not implemented",
      ...args
    );
    return this;
  }

  addIntegrationMiddleware(...args) {
    console.warn(
      "Analytics: addIntegrationMiddleware method not implemented",
      ...args
    );
    return this;
  }

  setAnonymousId(...args) {
    console.warn("Analytics: setAnonymousId method not implemented", ...args);
    return this;
  }

  addDestinationMiddleware(...args) {
    console.warn(
      "Analytics: addDestinationMiddleware method not implemented",
      ...args
    );
    return this;
  }

  register(...args) {
    console.warn("Analytics: register method not implemented", ...args);
    return this;
  }
}

const analytics = new Analytics();

if (typeof window !== "undefined") {
  window.analytics = analytics;
}

export default analytics;
